import axios, { AxiosError, AxiosResponse } from 'axios';
import { clearRememberMe } from '@/helpers/sessionTimeout';

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_APP_API_URL + '/api/',
});

apiClient.interceptors.request.use(async function (config) {
  if (config.headers) {
    config.headers['Content-Type'] = 'application/json';
  }
  return config;
});

/**
 * An /api/ path this server does not have does not answer 404: the SPA is the fallback route, so it
 * answers **200 with index.html**. Axios calls that a success, the caller returns `response.data`, and a
 * screen that just asked "to delete permanently?" closes with nothing deleted and nothing said.
 *
 * That is not hypothetical. On 05/10/2026 a production deploy picked a backend build from three weeks
 * earlier; the endpoint behind the office's "delete the ID appendix" button did not exist in it, and the
 * secretary confirmed a destructive action that never happened - without one error on screen (HAT-1302).
 *
 * `assertJsonBody` in ApiCalls cannot catch this. It only inspects bodies whose Content-Type says JSON,
 * and this one says HTML. No endpoint in this API answers with an HTML document, so an HTML body is never
 * a real answer - whatever the status line says.
 */
const isSpaFallback = (response: AxiosResponse) =>
  String(response.headers?.['content-type'] ?? '').toLowerCase().includes('text/html');

/**
 * Deliberately not an AxiosError and deliberately carrying no `response`: the screens read an error's
 * `response.data` to show the server's own words, and here that data is an entire HTML page.
 */
export class ApiRouteMissingError extends Error {
  constructor(public readonly request?: string) {
    super('הפעולה לא בוצעה: השרת לא זיהה את הבקשה. ייתכן שהאתר עודכן — יש לרענן את הדף ולנסות שוב.');
    this.name = 'ApiRouteMissingError';
  }
}

// Response interceptor
apiClient.interceptors.response.use(
  (response) => {
    if (isSpaFallback(response)) {
      const request = `${response.config?.method?.toUpperCase() ?? ''} ${response.config?.url ?? ''}`.trim();
      // Logged as an error because this is a deployment fault, not a user one: the screen is addressing an
      // endpoint the running server does not have.
      console.error(`The API answered with the SPA page - this route does not exist on the server: ${request}`);
      return Promise.reject(new ApiRouteMissingError(request));
    }
    return response;
  },
  (error: AxiosError) => {
    if (error.response && error.response.status === 401) {
      // Redirect to login page on 401 Unauthorized
      localStorage.removeItem('currentUser');
      localStorage.removeItem('sessionLastActivityAt');
      clearRememberMe();
      window.location.href = '/new/login';
    }
    return Promise.reject(error);
  }
);

export default apiClient;
